import {
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as TeclaReact,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { IconoCheck } from './iconos';
import './ui.css';

/**
 * Lo que puede ir dentro de un menú. Salen de las dos variantes del Figma:
 * «Menú de fila» (Editar, Duplicar, divisor, Desactivar) y «Más acciones»
 * (Importar, grupo EXPORTAR, Excel, PDF y una nota al pie).
 *
 * `marcada` convierte la opción en una de varias excluyentes —el idioma dentro
 * del menú de la sesión—: lleva el check y el lector de pantalla la anuncia
 * como elegida. Sin `marcada`, es una acción común.
 */
export type ElementoMenu =
  | {
      tipo?: 'opcion';
      etiqueta: string;
      onSeleccionar: () => void;
      peligro?: boolean;
      marcada?: boolean;
      /** Un dato corto al final del renglón, en gris: «Principal» en el selector de sucursal. */
      detalle?: string;
      /**
       * Se ve en gris y no hace nada: lo que existe en el diseño pero todavía no
       * en la API —«Exportar a Excel» de clientes, hasta #107—. Una nota del
       * mismo menú debería decir por qué.
       */
      deshabilitada?: boolean;
    }
  | { tipo: 'divisor' }
  | { tipo: 'grupo'; etiqueta: string }
  | { tipo: 'nota'; texto: string };

interface Props {
  elementos: readonly ElementoMenu[];
  /**
   * El botón que abre el menú. `atributos` va tal cual sobre el botón: son los
   * ARIA que le dicen al lector de pantalla que abre un menú y si está abierto.
   *
   *   disparador={({ alternar, atributos }) => (
   *     <Boton soloIcono aria-label="Más acciones" onClick={alternar} {...atributos}>•••</Boton>
   *   )}
   */
  disparador: (props: {
    abierto: boolean;
    alternar: (evento: MouseEvent<HTMLElement>) => void;
    atributos: {
      'aria-haspopup': 'menu';
      'aria-expanded': boolean;
      'aria-controls': string;
    };
  }) => ReactNode;
  /** Ancho del menú en px. El Figma usa 190 para el de fila y 230 para «Más acciones». */
  ancho?: number;
}

/** Lo que se reserva para decidir si el menú cabe abajo del botón. */
const ALTO_ESTIMADO = 240;

/**
 * Un menú desplegable anclado a su botón, alineado a la derecha.
 *
 * **Va en posición fija, no absoluta.** El «•••» de fila vive dentro de una
 * celda que recorta lo que se sale y de un marco con desplazamiento lateral;
 * un menú absoluto quedaba cortado por los dos. Fijo, se dibuja sobre todo, y
 * por eso se cierra al desplazar la página: si no, se quedaría flotando lejos
 * de su fila. Si no cabe abajo, se abre hacia arriba.
 *
 * Se cierra al elegir una opción, al tocar afuera y con Escape. Al cerrarse con
 * Escape o al elegir, el foco vuelve al botón: sin eso, quien navega con teclado
 * queda parado en un elemento que ya no existe.
 *
 * ## Teclado (#79)
 *
 * Un lector de pantalla que entra a un `role="menu"` espera recorrerlo con
 * flechas, no con Tab. Al abrir, el foco va a la primera opción; las flechas,
 * Inicio y Fin lo mueven entre las opciones —que tienen `tabIndex={-1}`: solo se
 * llega a ellas así—, y Tab sale del menú y lo cierra.
 */
export function Menu({ elementos, disparador, ancho = 190 }: Props) {
  const [posicion, setPosicion] = useState<CSSProperties | null>(null);
  const abierto = posicion !== null;
  const raiz = useRef<HTMLDivElement>(null);
  const id = useId();

  // Se mide el botón que recibió el clic, no el ref: leer un ref desde algo que
  // se entrega durante el render es lo que la regla de hooks prohíbe.
  function alternar(evento: MouseEvent<HTMLElement>) {
    if (abierto) {
      setPosicion(null);
      return;
    }
    const caja = evento.currentTarget.getBoundingClientRect();
    const left = Math.max(8, caja.right - ancho);
    setPosicion(
      window.innerHeight - caja.bottom >= ALTO_ESTIMADO
        ? { top: caja.bottom + 4, left, width: ancho }
        : { bottom: window.innerHeight - caja.top + 4, left, width: ancho },
    );
  }

  /** Las opciones que reciben foco, en orden: los divisores, grupos y notas no. */
  const opciones = () =>
    Array.from(raiz.current?.querySelectorAll<HTMLButtonElement>('[role^="menuitem"]') ?? []);
  const botonDelMenu = () => raiz.current?.querySelector<HTMLButtonElement>('button');

  // Al abrir, el foco a la primera opción: desde ahí se sigue con las flechas.
  useEffect(() => {
    if (abierto) raiz.current?.querySelector<HTMLButtonElement>('[role^="menuitem"]')?.focus();
  }, [abierto]);

  function alTeclearOpciones(evento: TeclaReact<HTMLDivElement>) {
    const lista = opciones();
    const actual = lista.indexOf(document.activeElement as HTMLButtonElement);
    const ir = (i: number) => {
      evento.preventDefault();
      lista[(i + lista.length) % lista.length]?.focus();
    };
    if (evento.key === 'ArrowDown') ir(actual + 1);
    else if (evento.key === 'ArrowUp') ir(actual - 1);
    else if (evento.key === 'Home') ir(0);
    else if (evento.key === 'End') ir(lista.length - 1);
    // Tab sigue su camino natural, pero el menú no se queda abierto detrás.
    else if (evento.key === 'Tab') setPosicion(null);
  }

  useEffect(() => {
    if (!abierto) return;

    const cerrar = () => setPosicion(null);
    function alTocar(evento: PointerEvent) {
      if (!raiz.current?.contains(evento.target as Node)) cerrar();
    }
    function alTeclear(evento: KeyboardEvent) {
      if (evento.key !== 'Escape') return;
      cerrar();
      raiz.current?.querySelector<HTMLButtonElement>('button')?.focus();
    }

    document.addEventListener('pointerdown', alTocar);
    document.addEventListener('keydown', alTeclear);
    // `capture` para enterarse también del desplazamiento del marco de la tabla,
    // que no burbujea hasta el documento.
    window.addEventListener('scroll', cerrar, { capture: true });
    window.addEventListener('resize', cerrar);
    return () => {
      document.removeEventListener('pointerdown', alTocar);
      document.removeEventListener('keydown', alTeclear);
      window.removeEventListener('scroll', cerrar, { capture: true });
      window.removeEventListener('resize', cerrar);
    };
  }, [abierto]);

  return (
    <div className="ui-menu" ref={raiz}>
      {disparador({
        abierto,
        alternar,
        atributos: {
          'aria-haspopup': 'menu',
          'aria-expanded': abierto,
          'aria-controls': id,
        },
      })}

      {posicion && (
        <div id={id} role="menu" className="ui-menu__panel" style={posicion} onKeyDown={alTeclearOpciones}>
          {elementos.map((elemento, i) => {
            switch (elemento.tipo) {
              case 'divisor':
                return <hr key={i} className="ui-menu__divisor" />;
              case 'grupo':
                return (
                  <div key={i} className="ui-menu__grupo" role="presentation">
                    {elemento.etiqueta}
                  </div>
                );
              case 'nota':
                return (
                  <p key={i} className="ui-menu__nota">
                    {elemento.texto}
                  </p>
                );
              default: {
                const excluyente = elemento.marcada !== undefined;
                return (
                  <button
                    key={i}
                    type="button"
                    role={excluyente ? 'menuitemradio' : 'menuitem'}
                    tabIndex={-1}
                    aria-checked={excluyente ? elemento.marcada : undefined}
                    className={`ui-menu__opcion${elemento.peligro ? ' ui-menu__opcion--peligro' : ''}`}
                    // `aria-disabled` y no `disabled`: las flechas siguen parando en
                    // ella y el lector la anuncia como no disponible, así quien no ve
                    // el menú se entera de que existe. Por lo mismo, el clic llega y
                    // hay que ignorarlo: sin cerrar el menú, que no parezca elegida.
                    aria-disabled={elemento.deshabilitada}
                    onClick={() => {
                      if (elemento.deshabilitada) return;
                      setPosicion(null);
                      // Antes de la acción: si abre una ventana, esa toma el foco después.
                      botonDelMenu()?.focus();
                      elemento.onSeleccionar();
                    }}
                  >
                    {elemento.etiqueta}
                    {elemento.detalle && <span className="ui-menu__detalle">{elemento.detalle}</span>}
                    {elemento.marcada && <IconoCheck />}
                  </button>
                );
              }
            }
          })}
        </div>
      )}
    </div>
  );
}
